import { useEffect, useMemo } from 'react';
import { ISurveyResponse } from '../shared/interfaces/ISurveyResponse';
import { useTranslation } from 'react-i18next';
import DateUtils from '../shared/DateUtils';
import SurveyUtils from '../shared/SurveyUtils';

const COLORS = {
  total: '#03424f',
  promoter: '#74da67',
  yes: '#74DA6770',
  positive: 'rgba(159,218,103,0.44)',
  neutral: '#F7B419',
  detractor: '#E15E5EFF',
  no: '#E15E5E70',
  negative: 'rgba(225,94,133,0.44)',
};

interface ChartDataItem {
  name: string;
  surveys: number;
  color: string;
  mode: string;
  question?: string;
  positive?: number | null;
  neutral?: number | null;
  negative?: number | null;
}

interface IAnswer {
  createdAt: string;
  rating: number;
  feedback?: string;
  answer?: number;
  questionId?: string;
}

const useChartData = (
  surveyResponse: ISurveyResponse[],
  type: string,
  mode: string,
  customAnswers?: {
    answerOne: string;
    answerTwo: string;
    answerThree: string;
    answerFour: string;
    answerFive: string;
  }
) => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  useEffect(() => {
    const handleLanguageChange = () => {
      window.location.reload();
    };

    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  const calculateNPSChartData = (responses: ISurveyResponse[]): ChartDataItem[] => {
    if (mode === t('total')) {
      const npsAllAnswers = responses.flatMap((response) => response.answers);
      const total = npsAllAnswers.length;
      const promoter = npsAllAnswers.filter((item) => item.rating >= 9).length;
      const neutral = npsAllAnswers.filter((item) => item.rating >= 7 && item.rating <= 8).length;
      const detractor = npsAllAnswers.filter((item) => item.rating <= 6).length;

      return [
        { name: 'Gesamtanzahl', surveys: total, color: COLORS.total, mode: t('total') },
        {
          name: 'positiv (Score 9 + 10)',
          surveys: promoter,
          color: COLORS.promoter,
          mode: t('total'),
        },
        {
          name: 'neutral (Score 7 + 8)',
          surveys: neutral,
          color: COLORS.neutral,
          mode: t('total'),
        },
        {
          name: 'negativ (Score 0 - 6)',
          surveys: detractor,
          color: COLORS.detractor,
          mode: t('total'),
        },
      ];
    } else if (mode === 'month') {
      return calculateYearlyChartData(responses as ISurveyResponse[], 'NPS', currentLanguage);
    }
    return [];
  };

  const calculateBarometerChartData = (responses: ISurveyResponse[]): ChartDataItem[] => {
    if (mode === t('total')) {
      let positiveCount = 0;
      let neutralCount = 0;
      let negativeCount = 0;
      let total = 0;

      responses.forEach((survey) => {
        if (survey.mood === 'POSITIVE') positiveCount += survey.answers.length;
        else if (survey.mood === 'NEUTRAL') neutralCount += survey.answers.length;
        else if (survey.mood === 'NEGATIVE') negativeCount += survey.answers.length;
        total += survey.answers.length;
      });

      return [
        { name: 'Gesamtanzahl', surveys: total, color: COLORS.total, mode: t('total') },
        { name: 'positive', surveys: positiveCount, color: COLORS.promoter, mode: t('total') },
        { name: 'neutral', surveys: neutralCount, color: COLORS.neutral, mode: t('total') },
        { name: 'negative', surveys: negativeCount, color: COLORS.detractor, mode: t('total') },
      ];
    } else if (mode === 'month') {
      return calculateYearlyChartData(responses as ISurveyResponse[], 'BAROMETER', currentLanguage);
    }
    return [];
  };

  const calculateCustomChartData = (responses: ISurveyResponse[]): ChartDataItem[] => {
    if (mode === t('total')) {
      const customEval = SurveyUtils.evaluateCustom(responses);
      return [
        { name: 'Gesamtanzahl', surveys: customEval.total, color: COLORS.total, mode: t('total') },
        {
          name: 'beendet',
          surveys: customEval.completed,
          color: COLORS.promoter,
          mode: t('total'),
        },
        {
          name: 'abgebrochen',
          surveys: customEval.abborted,
          color: COLORS.detractor,
          mode: t('total'),
        },
      ];
    } else if (mode === 'month') {
      return calculateYearlyChartData(
        responses as ISurveyResponse[],
        'CUSTOMSURVEY',
        currentLanguage
      );
    }
    return [];
  };

  const calculateStandardChartData = (responses: ISurveyResponse[]): ChartDataItem[] => {
    if (mode === t('total')) {
      let total = responses[0].totalCount;
      let positiveCount = 0;
      let negativeCount = 0;
      const score = responses[0].score;

      let minLength = Math.min(responses[0].answers.length, responses[1].answers.length);
      let sumOfPairs = [];

      for (let i = 0; i < minLength; i++) {
        sumOfPairs.push(responses[0].answers[i].rating + responses[1].answers[i].rating);
      }

      let lastAnswerArray = responses[2].answers.map((answer) => answer.rating);

      sumOfPairs.forEach((pair, index) => {
        if (index < lastAnswerArray.length) {
          if (score <= pair && lastAnswerArray[index] === 1) {
            positiveCount += 1;
          } else {
            negativeCount += 1;
          }
        }
      });

      return [
        { name: 'Gesamtanzahl', surveys: total, color: COLORS.total, mode: 'total' },
        {
          name: 'positive',
          surveys: positiveCount,
          color: COLORS.promoter,
          mode: 'total',
        },
        {
          name: 'negative',
          surveys: negativeCount,
          color: COLORS.detractor,
          mode: 'total',
        },
      ];
    } else if (mode === 'details') {
      if (customAnswers === undefined) return [];
      return responses.flatMap((response, index, array) => {
        const isLastEntry = index === array.length - 1;
        const dataPoints = [];
        const lastEntry = array[array.length - 1];

        if (!isLastEntry) {
          dataPoints.push(
            {
              name: customAnswers?.answerOne,
              surveys: response.answers.filter((rating) => rating.rating === 5).length,
              color: COLORS.promoter,
              mode: 'details',
            },
            {
              name: customAnswers?.answerTwo,
              surveys: response.answers.filter((rating) => rating.rating === 4).length,
              color: COLORS.positive,
              mode: 'details',
            },
            {
              name: customAnswers?.answerThree,
              surveys: response.answers.filter((rating) => rating.rating === 3).length,
              color: COLORS.neutral,
              mode: 'details',
            },
            {
              name: customAnswers?.answerFour,
              surveys: response.answers.filter((rating) => rating.rating === 2).length,
              color: COLORS.negative,
              mode: 'details',
            },
            {
              name: customAnswers?.answerFive,
              surveys: response.answers.filter((rating) => rating.rating === 1).length,
              color: COLORS.detractor,
              mode: 'details',
            }
          );
        } else {
          dataPoints.push(
            {
              name: t('yes'),
              surveys: lastEntry.answers.filter((answer) => answer.rating === 1).length,
              color: COLORS.yes,
              mode: 'details',
            },
            {
              name: t('no'),
              surveys: lastEntry.answers.filter((answer) => answer.rating === 0).length,
              color: COLORS.no,
              mode: 'details',
            }
          );
        }
        return dataPoints;
      });
    } else if (mode === 'month') {
      return calculateYearlyChartData(responses as ISurveyResponse[], 'STANDARD', currentLanguage);
    }
    return [];
  };

  const calculateYearlyChartData = (
    responses: ISurveyResponse[],
    type: string,
    currentLanguage: string
  ): ChartDataItem[] => {
    if (responses.length === 0) {
      return [];
    }

    const firstResponseDate = new Date(
      Math.min(
        ...responses
          .filter((response) => response.answers !== undefined)
          .flatMap((response) => response.answers)
          .map((answer) => answer.createdAt)
          .filter((date) => date !== undefined)
          .map((date) => new Date(date).getTime())
      )
    );
    const firstMonth = firstResponseDate.getMonth();
    const firstYear = firstResponseDate.getFullYear();

    const surveysPerMonth: {
      month: number;
      year: number;
      key: string;
      positive: number;
      neutral: number;
      negative: number;
    }[] = [];
    const now = new Date();
    const currentDate = new Date(now.getFullYear(), now.getMonth(), 1);
    let startMonth = firstMonth;
    let startYear = firstYear;
    let startDate = new Date(startYear, startMonth, 1);

    while (startDate.getTime() <= currentDate.getTime()) {
      surveysPerMonth.push({
        month: startMonth,
        year: startYear,
        key: DateUtils.shortMonthByIndex(startMonth, currentLanguage),
        positive: 0,
        neutral: 0,
        negative: 0,
      });

      startMonth += 1;
      if (startMonth > 11) {
        startMonth = 0;
        startYear += 1;
      }
      startDate = new Date(startYear, startMonth, 1);
    }

    if (type === 'CUSTOMSURVEY') {
      if (responses[0] && responses[0].answers && responses[0].answers.length) {
        const customEval = SurveyUtils.evaluateCustom(responses);

        Object.keys(customEval.responses).forEach((rid) => {
          const answers = customEval.responses[rid];
          const answerDate = new Date(answers[0].createdAt);

          const month = answerDate.getMonth();
          const year = answerDate.getFullYear();
          const monthData = surveysPerMonth.find((m) => m.month === month && m.year === year);

          if (monthData) {
            if (SurveyUtils.isCompleted(answers, responses)) {
              monthData.positive += 1;
            } else {
              monthData.negative += 1;
            }
          }
        });
      }
    }

    if (type === 'STANDARD') {
      if (responses[0] && responses[0].answers && responses[0].answers.length) {
        const score = responses[0]?.score || 0;
        for (let i = 0; i < responses[0].answers.length; i++) {
          const answer = responses[0].answers[i];
          const answerDate = new Date(answer?.createdAt);
          const month = answerDate.getMonth();
          const year = answerDate.getFullYear();
          const monthData = surveysPerMonth.find((m) => m.month === month && m.year === year);

          if (monthData) {
            const sumOfTwo = answer.rating + (responses[1]?.answers[i]?.rating || 0);
            const lastAnswer = responses[2]?.answers[i]?.rating || 0;
            if (score <= sumOfTwo && lastAnswer === 1) {
              monthData.positive += 1;
            } else {
              monthData.negative += 1;
            }
          }
        }
      }
    }

    if (type === 'NPS') {
      responses.forEach((response: ISurveyResponse) => {
        response.answers.forEach((answer: IAnswer) => {
          const answerDate = new Date(answer?.createdAt);
          const month = answerDate.getMonth();
          const year = answerDate.getFullYear();
          const monthData = surveysPerMonth.find((m) => m.month === month && m.year === year);

          if (monthData) {
            if (answer.rating >= 9) {
              monthData.positive += 1;
            } else if (answer.rating >= 7 && answer.rating <= 8) {
              monthData.neutral += 1;
            } else if (answer.rating <= 6) {
              monthData.negative += 1;
            } else {
              return;
            }
          }
        });
      });
    }

    if (type === 'BAROMETER') {
      responses.forEach((response: ISurveyResponse) => {
        response.answers.forEach((answer: IAnswer) => {
          const answerDate = new Date(answer?.createdAt);
          const month = answerDate.getMonth();
          const year = answerDate.getFullYear();
          const monthData = surveysPerMonth.find((m) => m.month === month && m.year === year);

          if (!monthData) return;

          if (response.mood === 'POSITIVE') {
            monthData.positive += 1;
          } else if (response.mood === 'NEUTRAL') {
            monthData.neutral += 1;
          } else {
            monthData.negative += 1;
          }
        });
      });
    }

    const result = surveysPerMonth.map((monthData) => ({
      name: monthData.key,
      positive: monthData.positive || null,
      neutral: monthData.neutral || null,
      negative: monthData.negative || null,
      surveys: monthData.positive + monthData.neutral + monthData.negative,
      color: COLORS.total,
      mode: 'month',
    }));
    return result;
  };

  return useMemo(() => {
    switch (type) {
      case 'NPS':
        return calculateNPSChartData(surveyResponse);
      case 'BAROMETER':
        return calculateBarometerChartData(surveyResponse);
      case 'STANDARD':
        return calculateStandardChartData(surveyResponse);
      case 'CUSTOMSURVEY':
        return calculateCustomChartData(surveyResponse);
      default:
        return [];
    }
  }, [surveyResponse, type, mode]);
};

export default useChartData;
